import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import { HouseListRepository } from '@/repository/list/impl/HouseListRepository';
import { HouseListService } from '@/service/list/impl/HouseListService';
import { BoughtListService } from '@/service/list/impl/BoughtListService';
import { BoughtListRepository } from '@/repository/list/impl/BoughtListRepository';
import { CustomRouteObject, RouteService } from '@/service/RouteService.ts';
import { ExportJobRepository } from '@/repository/ExportJobRepository.ts';
import { ExportJobService } from '@/service/ExportJobService.ts';
import { UserFilterService } from '@/service/UserFilterService.ts';
import { UserFilterRepository } from '@/repository/UserFilterRepository.ts';
import { ReportScheduleRepository } from '@/repository/ReportScheduleRepository.ts';
import { ReportScheduleService } from '@/service/ReportScheduleService.ts';
import { CognitoUserService } from '@/service/CognitoUserService.ts';
import { DataTransferJobService } from '@/service/DataTransferJobService.ts';
import { DataTransferJobRepository } from '@/repository/DataTransferJobRepository.ts';
import { StaffSeedService } from '@/service/list/impl/StaffSeedService';
import { StaffSeedRepository } from '@/repository/list/impl/StaffSeedRepository';
import { ExclusionRuleRepository } from '@/repository/ExclusionRuleRepository';
import { ExclusionRuleService } from '@/service/ExclusionRuleService';
import { ExclusionCacheService } from '@/service/ExclusionCacheService';
import { FlagDefinitionService } from '@/service/FlagDefinitionService';

export interface ServiceContextProps {
  userFilterService: UserFilterService;
  houseListService: HouseListService;
  boughtListService: BoughtListService;
  routeService: RouteService;
  exportJobService: ExportJobService;
  reportScheduleService: ReportScheduleService;
  cognitoUserService: CognitoUserService;
  dataTransferJobService: DataTransferJobService;
  staffSeedService: StaffSeedService;
  exclusionRuleService: ExclusionRuleService;
  exclusionCacheService: ExclusionCacheService;
  flagDefinitionService: FlagDefinitionService;
}

interface ServiceProviderProps {
  routes?: CustomRouteObject[];
  serviceContext?: ServiceContextProps;
  children: ReactNode;
}

// Create the context with default values
export const ServiceContext = createContext<ServiceContextProps | undefined>(
  undefined
);

// Create a provider component
export const ServiceProvider: React.FC<ServiceProviderProps> = ({
  routes,
  serviceContext,
  children,
}) => {
  const userFilterService = useMemo(
    () => new UserFilterService(new UserFilterRepository()),
    []
  );
  const houseListService = useMemo(
    () => new HouseListService(new HouseListRepository()),
    []
  );
  const boughtListService = useMemo(
    () => new BoughtListService(new BoughtListRepository()),
    []
  );
  const routeService = useMemo(() => new RouteService(routes), [routes]);
  const exportJobService = useMemo(
    () => new ExportJobService(new ExportJobRepository()),
    []
  );
  const reportScheduleService = useMemo(
    () => new ReportScheduleService(new ReportScheduleRepository()),
    []
  );
  const cognitoUserService = useMemo(() => new CognitoUserService(), []);
  const dataTransferJobService = useMemo(
    () => new DataTransferJobService(new DataTransferJobRepository()),
    []
  );
  const staffSeedService = useMemo(
    () => new StaffSeedService(new StaffSeedRepository()),
    []
  );
  const exclusionRuleService = useMemo(
    () => new ExclusionRuleService(new ExclusionRuleRepository()),
    []
  );
  const exclusionCacheService = useMemo(
    () => new ExclusionCacheService(exclusionRuleService),
    [exclusionRuleService]
  );
  const flagDefinitionService = useMemo(() => new FlagDefinitionService(), []);

  return (
    <ServiceContext.Provider
      value={
        serviceContext
          ? serviceContext
          : {
              userFilterService,
              houseListService,
              boughtListService,
              routeService,
              exportJobService,
              reportScheduleService,
              cognitoUserService,
              dataTransferJobService,
              staffSeedService,
              exclusionRuleService,
              exclusionCacheService,
              flagDefinitionService,
            }
      }
    >
      {children}
    </ServiceContext.Provider>
  );
};

// Create a custom hook to use the service context
export const useServices = () => {
  const context = useContext(ServiceContext);
  if (!context) {
    throw new Error('useServices must be used within a ServiceProvider');
  }
  return context;
};
